/**
 * Fechas y horas en la zona que declara el informe.
 *
 * El motor publica los instantes en UTC (`toISOString`): es lo correcto para
 * calcular y para el CSV, que lleva la `Z`. Lo que se le muestra a una persona,
 * en cambio, va en la zona que el informe declara en su encabezado. Antes se
 * cortaba la cadena ISO (`slice(11, 19)`), que da la hora UTC y pierde el
 * cambio de día: una frenada de las 22:10 en Bogotá salía como 03:10 del día
 * siguiente, sin fecha.
 *
 * La conversión usa `Intl.DateTimeFormat` con la zona explícita. No lee el
 * reloj ni la zona de la máquina: el mismo instante da el mismo texto en el
 * Worker, en el navegador y en las pruebas.
 */

import { parseStrictIsoTimestamp } from "../pipeline/analysis-period.ts";

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

/** Fecha (`dd/mm/aaaa`) y hora (`HH:MM:SS`) del instante en esa zona, o `null` si no es una marca de tiempo. */
export function zonedParts(iso: string, timeZone: string): { date: string; time: string } | null {
  const epochMs = parseStrictIsoTimestamp(iso);
  if (epochMs === null) return null;
  const parts = new Map(formatterFor(timeZone).formatToParts(new Date(epochMs)).map((part) => [part.type, part.value]));
  return {
    date: `${parts.get("day")}/${parts.get("month")}/${parts.get("year")}`,
    time: `${parts.get("hour")}:${parts.get("minute")}:${parts.get("second")}`,
  };
}

/** `dd/mm/aaaa HH:MM:SS` en la zona. Si el valor no es una marca de tiempo, se devuelve tal cual. */
export function zonedDateTime(iso: string, timeZone: string): string {
  const parts = zonedParts(iso, timeZone);
  return parts ? `${parts.date} ${parts.time}` : iso;
}

/** `dd/mm/aaaa` en la zona. Si el valor no es una marca de tiempo, se devuelve tal cual. */
export function zonedDate(iso: string, timeZone: string): string {
  return zonedParts(iso, timeZone)?.date ?? iso;
}
